import { useRef, useState } from "react";
import { UploadCloud, FileIcon, X, CheckCircle2, AlertTriangle, ExternalLink } from "lucide-react";
import { uploadFileInChunks } from "@/components/setup/chunkedUpload";
import { imageSpecs, specsPdf } from "@/data/assetSpecs";
import { useLang } from "@/i18n";

const ACCEPT =
  ".png,.jpg,.jpeg,.webp,.svg,.gif,.heic,.heif,.pdf,.ai,.eps,.zip,.xlsx,.xls,.csv,.doc,.docx,.ppt,.pptx,.ttf,.otf,.woff,.woff2,.mp4,.mov";

const CHECKLIST = [
  "Official Coca-Cola HBC logo for your business unit (vector — AI / EPS / SVG)",
  "Campaign visuals & assets you already have — banners, popup ads, packshots",
  "Product & ambience photos",
  "Local brand guidelines (if any)",
];

const fmtSize = (bytes) => {
  const mb = bytes / (1024 * 1024);
  return mb >= 1 ? `${mb.toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;
};

export const StepAssets = ({ files, onAdd, onRemove, showModeHint = true }) => {
  const { t } = useLang();
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState([]); // [{key, name, progress, error}]
  const [dragOver, setDragOver] = useState(false);

  const startUploads = (fileList) => {
    Array.from(fileList).forEach((file) => {
      const key = `${file.name}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
      setUploading((u) => [...u, { key, name: file.name, progress: 0, error: null }]);
      uploadFileInChunks(file, (pct) =>
        setUploading((u) => u.map((it) => (it.key === key ? { ...it, progress: pct } : it)))
      )
        .then((meta) => {
          setUploading((u) => u.filter((it) => it.key !== key));
          onAdd({ file_id: meta.file_id, filename: meta.filename, size: meta.size });
        })
        .catch((err) => {
          setUploading((u) => u.map((it) => (it.key === key ? { ...it, error: err.message } : it)));
        });
    });
  };

  return (
    <div className="space-y-6" data-testid="setup-step-assets">
      {/* Checklist */}
      <div className="bg-[#fafafa] border border-[#e8e8e8] rounded-sm p-5">
        <span className="cchbc-eyebrow mb-3">{t("What to include")}</span>
        <ul className="mt-3 space-y-1.5">
          {CHECKLIST.map((c) => (
            <li key={c} className="flex items-center gap-2 text-sm text-[#555555]">
              <CheckCircle2 className="w-3.5 h-3.5 text-[#dd260e] shrink-0" strokeWidth={2} />
              {t(c)}
            </li>
          ))}
        </ul>
      </div>

      {/* Technical specifications (same as Digital Menu page) */}
      <div className="bg-white border border-[#e8e8e8] rounded-sm overflow-hidden" data-testid="setup-asset-specs">
        <div className="flex items-center justify-between bg-[#000000] text-white px-4 py-2.5">
          <span className="text-[10px] font-semibold uppercase tracking-wider">{t("Technical specifications")}</span>
          <a
            href={specsPdf}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-white/80 hover:text-white"
            data-testid="setup-asset-specs-pdf"
          >
            {t("Full specs (PDF)")} <ExternalLink className="w-3 h-3" />
          </a>
        </div>
        <div className="hidden sm:grid grid-cols-[1.2fr_1.4fr_1fr_0.8fr] bg-[#fafafa] border-b border-[#e8e8e8]">
          {["Type", "Formats", "Resolution", "Ratio"].map((h) => (
            <div key={h} className="px-4 py-2 text-[10px] font-bold uppercase tracking-[0.1em] text-[#999999]">
              {t(h)}
            </div>
          ))}
        </div>
        {imageSpecs.map((s, i) => (
          <div
            key={s.type}
            className={`grid grid-cols-2 sm:grid-cols-[1.2fr_1.4fr_1fr_0.8fr] ${i < imageSpecs.length - 1 ? "border-b border-[#e8e8e8]" : ""}`}
          >
            <div className="px-4 py-2.5 text-xs font-bold text-[#000000]">{s.type}</div>
            <div className="px-4 py-2.5 text-xs text-[#555555]">{s.format}</div>
            <div className="px-4 py-2.5 text-xs text-[#555555]">{s.resolution}</div>
            <div className="px-4 py-2.5 text-xs text-[#555555]">{s.ratio}</div>
          </div>
        ))}
      </div>

      {/* Drop zone */}
      <div
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          if (e.dataTransfer.files?.length) startUploads(e.dataTransfer.files);
        }}
        className={`border-2 border-dashed rounded-sm p-10 flex flex-col items-center justify-center text-center cursor-pointer transition-colors ${
          dragOver ? "border-[#dd260e] bg-[#dd260e]/5" : "border-[#cccccc] hover:border-[#000000] bg-white"
        }`}
        data-testid="setup-upload-dropzone"
      >
        <UploadCloud className="w-8 h-8 text-[#dd260e] mb-3" strokeWidth={1.6} />
        <p className="text-sm font-bold text-[#000000] uppercase tracking-wide">{t("Drop files here or click to browse")}</p>
        <p className="text-xs text-[#999999] mt-1.5">
          {t("Images, PDF, AI/EPS, Office files, ZIP, fonts, video — up to 100 MB per file")}
        </p>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept={ACCEPT}
          className="hidden"
          onChange={(e) => {
            if (e.target.files?.length) startUploads(e.target.files);
            e.target.value = "";
          }}
          data-testid="setup-upload-input"
        />
      </div>

      {/* In-progress uploads */}
      {uploading.map((u) => (
        <div key={u.key} className="bg-white border border-[#e8e8e8] rounded-sm p-4" data-testid="setup-upload-progress">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-[#000000] font-medium truncate">{u.name}</span>
            {u.error ? (
              <button
                onClick={() => setUploading((list) => list.filter((it) => it.key !== u.key))}
                className="text-[#999999] hover:text-[#dd260e]"
                aria-label="Dismiss"
                data-testid="setup-upload-dismiss-error"
              >
                <X className="w-4 h-4" />
              </button>
            ) : (
              <span className="text-xs text-[#555555]">{u.progress}%</span>
            )}
          </div>
          {u.error ? (
            <p className="flex items-center gap-1.5 text-xs text-[#dd260e] font-semibold">
              <AlertTriangle className="w-3.5 h-3.5" /> {u.error}
            </p>
          ) : (
            <div className="w-full h-1.5 bg-[#e8e8e8] rounded-full overflow-hidden">
              <div className="h-full bg-[#dd260e] rounded-full transition-all" style={{ width: `${u.progress}%` }} />
            </div>
          )}
        </div>
      ))}

      {/* Uploaded files */}
      {files.length > 0 && (
        <div className="bg-white border border-[#e8e8e8] rounded-sm overflow-hidden" data-testid="setup-uploaded-files">
          <div className="bg-[#000000] text-white px-4 py-2.5 text-[10px] font-semibold uppercase tracking-wider">
            {t("Uploaded assets")} ({files.length})
          </div>
          {files.map((f, i) => (
            <div
              key={f.file_id}
              className={`flex items-center gap-3 px-4 py-3 ${i < files.length - 1 ? "border-b border-[#e8e8e8]" : ""}`}
              data-testid={`setup-uploaded-file-${i}`}
            >
              <FileIcon className="w-4 h-4 text-[#dd260e] shrink-0" strokeWidth={1.8} />
              <span className="text-sm text-[#000000] truncate flex-1">{f.filename}</span>
              <span className="text-xs text-[#999999] shrink-0">{fmtSize(f.size)}</span>
              <button
                onClick={() => onRemove(f.file_id)}
                className="text-[#999999] hover:text-[#dd260e] transition-colors shrink-0"
                aria-label={`Remove ${f.filename}`}
                data-testid={`setup-remove-file-${i}`}
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      {showModeHint && (
        <p className="text-[11px] text-[#999999] leading-relaxed">
          {t("Assets change over time — whenever there are updates, come back to this page and use")}{" "}
          <b className="text-[#555555]">"{t("Assets only")}"</b> {t("mode to send new or revised files without filling the whole form again.")}
        </p>
      )}
    </div>
  );
};
